import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import { withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import { useTheme } from 'lib/context/ThemeContext';

import classNames from 'classnames';
import { ARProductCardTheme } from './ARProductCard.theme';
import ImageWrapper from 'src/helpers/Media/ImageWrapper';
import ARProductModal from 'src/helpers/Modals/ARProductModal';
import { getEnum } from 'lib/utils/get-enum';
import SvgIcon, { IconTypes } from 'src/helpers/SvgIcon/SvgIcon';
import { useEffect, useRef, useState } from 'react';
import { useModalIdContext } from 'lib/context/GenericModalIDContext';
import { scrollVisualizerCardIntoView } from 'lib/utils/scroll-visualizer-card-utils';

export type ARProductCardProps = Feature.EnterpriseWeb.RenewalByAndersen.Cards.ARProductCard;

const ARProductCard = (props: ARProductCardProps): JSX.Element | null => {
  const { fields } = props;
  const { themeData } = useTheme(ARProductCardTheme);
  const _icon = getEnum<IconTypes>(fields?.buttonIcon);
  const modalId = 'arProductModal_' + fields?.productName?.value.replaceAll(' ', '');
  const [isARModalOpen, setIsARModalOpen] = useState(false);
  const { setSelectedModalId } = useModalIdContext();

  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const href = window.location.href;

    const queryString = href.split('?')[1];
    if (queryString) {
      const params = new URLSearchParams(queryString);
      const modalFromUrl = params.get('modalId');
      const arEnabled = params.get('ar') === 'true';

      if (arEnabled && modalFromUrl === modalId) {
        scrollVisualizerCardIntoView(cardRef.current);
        handleOpenARModal();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalId]);

  if (!fields || !fields.productImageThumbnail?.value?.src) {
    return null;
  }

  const { productName, productImageThumbnail } = fields;

  const viewInARBtn = (btnClasses: string) => {
    return (
      <>
        <div className={themeData.classes.buttonContainer}>
          <div
            className={classNames(
              'mt-s items-center justify-center',
              'flex items-start md:flex-row md:items-center'
            )}
          >
            {fields?.buttonText && (
              <div
                className={classNames(
                  'mt-s items-center justify-center',
                  'mb-s flex items-start md:flex-row md:items-center md:space-x-4'
                )}
              >
                <button
                  onClick={() => {
                    handleOpenARModal();
                  }}
                  className={classNames(
                    btnClasses,
                    'group relative flex !w-full items-center justify-center p-s',
                    'font-serif text-button font-bold text-theme-btn-text md:!w-fit'
                  )}
                >
                  <span>{fields?.buttonText?.value}</span>
                  <SvgIcon
                    icon={_icon}
                    className={classNames(
                      'ml-xxs',
                      'ml-[10px] group-hover:text-theme-btn-primary-icon-hover group-disabled:text-dark-gray',
                      btnClasses === ' bg-dark-gray' ? 'text-gray' : ' text-theme-btn-primary-icon'
                    )}
                  />
                </button>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };
  const handleOpenARModal = () => {
    setIsARModalOpen(true);
    setSelectedModalId(modalId);
  };

  const handleCloseARModal = () => {
    setIsARModalOpen(false);
    setSelectedModalId('');
  };

  return (
    <div id={modalId} ref={cardRef} className={classNames(themeData.classes.cardWrapper)}>
      {productName?.value && (
        <div className="">
          <h3 className={classNames(themeData.classes.headline)}>{productName?.value}</h3>
        </div>
      )}
      <div className={classNames(themeData.classes.immageWrapper)}>
        <ImageWrapper
          image={productImageThumbnail}
          imageLayout="responsive"
          additionalDesktopClasses="max-w-full"
          additionalMobileClasses="max-w-full"
        ></ImageWrapper>
      </div>
      {isARModalOpen && (
        <ARProductModal
          modalId={modalId}
          isModalOpen={isARModalOpen}
          setIsModalOpen={handleCloseARModal}
          {...props}
        />
      )}
      {viewInARBtn(
        ' bg-theme-btn-bg hover:bg-theme-btn-bg-hover hover:text-theme-btn-text-hover disabled:bg-gray disabled:text-dark-gray disabled:cursor-not-allowed'
      )}
    </div>
  );
};

export default withDatasourceCheck()<ARProductCardProps>(ARProductCard);
